import { GzCompanyListWebComponent, GzEnumFilterWebComponent } from '../../../index';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { CompanyFilterController } from '../controller/CompanyFilterController';
import { uniqBy } from 'lodash';
import { SelectedOption } from '../../gzEnumList/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { CompanySpecialValues } from '@nirvana/shared/dtos/types/shared/filters';
import { CompanyFilterEvents } from '../types/types';

export class GzCompanyFilterWebComponent extends GzEnumFilterWebComponent {
  public controller: CompanyFilterController;
  public defaultValue: Array<string>;
  public overwriteList;
  public specialValueSelected: string | undefined;
  public allDirectManagementLabel = '';

  @query('#multiple-selection-list')
  public readonly enumList!: GzCompanyListWebComponent;

  @property({ type: Boolean, reflect: true })
  showOnlySelectAll = false;

  @property({ type: Boolean, reflect: true })
  showOnlySelectAllDirectManagement = false;

  @property({ type: Boolean, reflect: true })
  showNone = false;

  private dependencies: any;

  injectDependencies(dependencies) {
    if (this.dependencies) {
      return;
    }
    this.dependencies = dependencies;
    this.controller = new CompanyFilterController(this.dependencies, this);
  }

  public handleLoadingState(shouldShowLoading: boolean) {
    this.enumList.isLoading = shouldShowLoading;
    this.setActionButtonDisabledState(shouldShowLoading);
  }

  public resetFilterLabel() {
    this.dropdownMenu.label = this.label;
    this.dropdownMenu.selectedValuesStrings = '';
  }

  protected renderEnumList() {
    let optionList;

    if (this.enumList === null || this.enumList.filterOptions === null || this.overwriteList) {
      optionList = this.filterOptions;
      this.overwriteList = false;
    } else {
      // we need to merge the arrays and if duplicates accept the enumlist.filterOption
      optionList = this.filterOptions.concat(this.enumList.filterOptions);
      optionList = uniqBy(optionList, JSON.stringify);

      optionList = optionList.map(item => {
        // an array with length of max two elements
        const sameItems = optionList.filter(sndItem => sndItem.value === item.value);
        if (sameItems.length === 1) {
          return item;
        } else {
          return this.enumList.filterOptions.find(option => option.value === item.value);
        }
      });

      // remove the duplicates of the above code
      optionList = uniqBy(optionList, JSON.stringify);
    }

    return html` <gz-company-list
      id="multiple-selection-list"
      .filterOptions="${optionList}"
      searchPlaceholderString="${this.searchPlaceholderString}"
      noSearchResultsString="${this.noSearchResultsString}"
      listTitle="${this.listTitle}"
      .canSelectAll="${this.canSelectAll}"
      .showOnlySelectAll="${this.showOnlySelectAll}"
      .showOnlySelectAllDirectManagement="${this.showOnlySelectAllDirectManagement}"
      .showNone="${this.showNone}"
      allItemsLabel="${this.allItemsLabel}"
      @optionChanged="${this.handleListOptionsChange}"
      allDirectManagementLabel="${this.allDirectManagementLabel}"
      class="enum-filter-list"
    ></gz-company-list>`;
  }

  protected handleDropdownClose(e: Event) {
    super.handleDropdownClose(e);
    if (this.isError) {
      this.dispatchEvent(new CustomEvent('close'));
    }
  }

  protected handleDropdownOpen() {
    if (this.isLoading || this.isError) {
      return;
    }

    const selectedValues = this.specialValueSelected
      ? this.controller.getSpecialSelectedOption(this.specialValueSelected as CompanySpecialValues)
      : this.enumList.getSelectedValuesFromFilterOptions();

    this.enumList.setSelectedOptions(selectedValues);
    super.handleDropdownOpen();
  }

  protected updateDropdownLabelWithSelectedValues(selectedOptions: SelectedOption[]) {
    // Handle special case of All for company filter
    const checkedOptions = isEmpty(selectedOptions) ? this.enumList.getCheckedOptions() : selectedOptions;

    this.dropdownMenu.label = this.label ?? '';
    if (isEmpty(checkedOptions)) {
      this.dropdownMenu.selectedValuesStrings = '';
      return;
    }

    const selectedOptionsText = checkedOptions.map(option => option.label).join(', ');
    this.dropdownMenu.selectedValuesStrings = selectedOptionsText;
  }

  protected setActionButtonDisabledState(isDisabled: boolean) {
    if (this.enumList.isLoading && !isDisabled) {
      return;
    }

    super.setActionButtonDisabledState(isDisabled);
  }

  public announceCompaniesChanged(): void {
    const event = new CustomEvent(CompanyFilterEvents.VALUES_UPDATED, {
      detail: {
        filterOptions: this.filterOptions,
        defaultValue: this.defaultValue,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}
